// useFeatureFlag.ts — reads a row from the key-value `feature_flags` table.
//
// M0 §4.1 confirmed the shape: `key TEXT PK, enabled BOOLEAN, description,
// updated_at`. Flags are ROWS, not columns, and RLS reads USING(true).
//
// ⚠️ This is a UI gate only. Per §6.5, a kill switch that only works in the UI
// is not a kill switch — anything with a server-side effect must check the flag
// in its Edge Function too. Here that is generate-parametric-preview, which
// re-checks `parametric_products_enabled` before creating a draft.
//
// Defaults to `false` while loading and on any error: an unreachable flag table
// must not open a gated path.

import { useState, useEffect } from "react";

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export function useFeatureFlag(key: string): { enabled: boolean; loading: boolean } {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${SB_URL}/rest/v1/feature_flags?select=enabled&key=eq.${encodeURIComponent(key)}&limit=1`,
          { headers: { apikey: ANON_KEY } },
        );
        if (!res.ok) throw new Error(`feature_flags ${res.status}`);
        const rows = await res.json();
        if (!cancelled) setEnabled(Array.isArray(rows) && rows[0]?.enabled === true);
      } catch {
        if (!cancelled) setEnabled(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [key]);

  return { enabled, loading };
}
